// Client-only custom ordering for Quick Replies (drag-to-reorder). No backend
// support for this yet — order is saved per-workspace to localStorage, so it
// persists across reloads on the same browser but isn't synced across devices
// or team members. If a backend `order` field ever gets added, this whole file
// can be swapped for a real API call without touching the components that use it.
import { useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "cd_quick_reply_order_";
const ORDER_CHANGE_EVENT = "quickReplyOrderChange";

function storageKey(workspaceId) {
  return `${STORAGE_PREFIX}${workspaceId}`;
}

export function getSavedQuickReplyOrder(workspaceId) {
  if (!workspaceId) return [];
  try {
    const raw = localStorage.getItem(storageKey(workspaceId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveQuickReplyOrder(workspaceId, orderedIds) {
  if (!workspaceId) return;
  try {
    localStorage.setItem(storageKey(workspaceId), JSON.stringify(orderedIds));
  } catch {
    // localStorage can throw (private mode, quota) — reordering just won't
    // persist across reloads this time, not worth surfacing to the user.
  }
  // Lets every mounted consumer (the Settings list AND the composer popup can
  // both be open-ish at once, or at least share the same saved order) pick up
  // a drag that happened somewhere else without a page reload.
  window.dispatchEvent(new CustomEvent(ORDER_CHANGE_EVENT, { detail: { workspaceId } }));
}

// Reorders `items` per the saved id sequence for workspaceId. Anything not in
// the saved order (a reply created after the last drag, or the very first
// render before any drag ever happened) is appended at the end, keeping its
// original (backend-sorted) relative order.
export function applyQuickReplyOrder(items, workspaceId) {
  const savedOrder = getSavedQuickReplyOrder(workspaceId);
  if (!savedOrder.length) return items;
  const byId = new Map(items.map((item) => [item._id, item]));
  const ordered = [];
  savedOrder.forEach((id) => {
    if (byId.has(id)) {
      ordered.push(byId.get(id));
      byId.delete(id);
    }
  });
  byId.forEach((item) => ordered.push(item)); // leftovers, original order preserved
  return ordered;
}

// Re-applies the saved order to `items`, re-deriving whenever the saved order
// changes (including a drag fired from a different mounted instance of this
// hook, via the window event) or the underlying items array changes.
export function useOrderedQuickReplies(items, workspaceId) {
  const [orderVersion, setOrderVersion] = useState(0);

  useEffect(() => {
    const handler = (e) => {
      if (!workspaceId || e.detail?.workspaceId === workspaceId) {
        setOrderVersion((v) => v + 1);
      }
    };
    window.addEventListener(ORDER_CHANGE_EVENT, handler);
    return () => window.removeEventListener(ORDER_CHANGE_EVENT, handler);
  }, [workspaceId]);

  // orderVersion is a deliberate re-derive trigger (bumped by the window event
  // above), not a real input to the computation itself.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => applyQuickReplyOrder(items, workspaceId), [items, workspaceId, orderVersion]);
}
